// The browser's side of the print server API (server/API.md, "Reader"): list and read jobs,
// decrypt them, rename, delete. All names and chunks are encrypted/decrypted here; the server
// only ever sees ciphertext. Only GET and POST are used (the nginx in front passes nothing else).
import { decryptChunks, decryptName, encryptName } from './crypto.js';

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export class ReaderClient {
  // baseUrl: '' for the server that served the page, or 'https://host' (then CORS applies: the API
  // is meant to be used same-origin). keys: from crypto.js (deriveFromKey/deriveFromReader).
  constructor({ baseUrl = '', keys, fetchFn = (...a) => fetch(...a) }) {
    const root = baseUrl.replace(/\/+$/, '');
    this.apiRoot = `${root}/api/v1`;
    this.base = `${this.apiRoot}/printers/${keys.id}`;
    this.keys = keys;
    this.fetch = fetchFn;
  }

  async request(method, path, { query, body } = {}) {
    const url = this.base + path + (query ? `?${new URLSearchParams(query)}` : '');
    const headers = { Authorization: `Bearer ${this.keys.readerAuth}` };
    const init = { method, headers };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const res = await this.fetch(url, init);
    if (!res.ok) {
      let code = 'error';
      let message = `HTTP ${res.status}`;
      try {
        const e = (await res.json()).error;
        code = e.code;
        message = e.message;
      } catch {
        // not a JSON error
      }
      throw new ApiError(res.status, code, message);
    }
    return res;
  }

  async name(nameEnc) {
    if (nameEnc == null) return null;
    try {
      return await decryptName(this.keys.enc, nameEnc);
    } catch {
      return null; // not readable with this key: treat as unnamed
    }
  }

  async job(j) {
    return { ...j, name: await this.name(j.name_enc) };
  }

  async printer() {
    const res = await this.request('GET', '');
    const info = await res.json();
    // The server's clock (Date header): last_seen_at is on that clock, not on this computer's.
    const date = res.headers?.get?.('Date');
    const serverNow = date ? Date.parse(date) : NaN;
    return { ...info, name: await this.name(info.name_enc), serverNow: Number.isNaN(serverNow) ? null : serverNow };
  }

  // Jobs with an id greater than `after`; includes the open job with its current size.
  async listJobs(after = 0, limit = 100) {
    const page = await (await this.request('GET', '/jobs', { query: { after, limit } })).json();
    return { jobs: await Promise.all(page.jobs.map((j) => this.job(j))), more: page.more };
  }

  // The decrypted chunks of a job after `cursor`: {chunks: [{boot, n, newJob, cut, bytes}], cursor, more, open, receivedAt}
  async stream(jobId, after = 0, max) {
    const res = await this.request('GET', `/jobs/${jobId}/stream`, { query: max ? { after, max } : { after } });
    const blobs = new Uint8Array(await res.arrayBuffer());
    return {
      chunks: await decryptChunks(this.keys.enc, blobs),
      cursor: Number(res.headers.get('X-Cursor')),
      more: res.headers.get('X-More') === '1',
      open: res.headers.get('X-Job-Open') === '1',
      receivedAt: Number(res.headers.get('X-Received-At')),
    };
  }

  // All the data of a job, as the concatenated stream bytes.
  async streamAll(jobId) {
    const parts = [];
    let cursor = 0;
    for (;;) {
      const page = await this.stream(jobId, cursor);
      for (const c of page.chunks) parts.push(c.bytes);
      cursor = page.cursor;
      if (!page.more) return { bytes: concat(parts), cursor, open: page.open };
    }
  }

  async newJob() {
    await this.request('POST', '/jobs/new', { body: {} });
  }

  // ids: a list of job ids, or 'all'. Unknown ids are ignored. Returns {deleted, stored_bytes}.
  async deleteJobs(ids) {
    if (ids !== 'all' && ids.length === 0) return { deleted: 0 };
    return (await this.request('DELETE', '/jobs', { query: ids === 'all' ? { all: '1' } : { ids: ids.join(',') } })).json();
  }

  async renameJob(id, name) {
    const nameEnc = name ? await encryptName(this.keys.enc, name) : null;
    return this.job(await (await this.request('PATCH', `/jobs/${id}`, { body: { name_enc: nameEnc } })).json());
  }

  async setName(name) {
    const nameEnc = name ? await encryptName(this.keys.enc, name) : null;
    return (await this.request('PATCH', '', { body: { name_enc: nameEnc } })).json();
  }

  async setEnabled(enabled) {
    return (await this.request('PATCH', '', { body: { enabled } })).json();
  }

  async deletePrinter() {
    await this.request('DELETE', '');
  }

  // ---- Web Push (server/API.md, "Notifications"): following this printer from this browser ----

  // The server's public key for push subscriptions (base64url, no credential needed).
  async pushKey() {
    const res = await this.fetch(`${this.apiRoot}/push/key`);
    if (!res.ok) throw new ApiError(res.status, 'error', `HTTP ${res.status}`);
    return (await res.json()).public_key;
  }

  // subscription: PushSubscription.toJSON() ({endpoint, keys: {p256dh, auth}}). Subscribing twice is fine.
  async subscribePush(subscription) {
    return (await this.request('POST', '/push', { body: { endpoint: subscription.endpoint, keys: subscription.keys } })).json();
  }

  // hash: sha256 of the endpoint, hex (push.js endpointHash). Resolves true if this browser follows the printer.
  async pushFollowed(hash) {
    try {
      await this.request('GET', `/push/${hash}`);
      return true;
    } catch (e) {
      if (e.status === 404) return false;
      throw e;
    }
  }

  async unsubscribePush(hash) {
    await this.request('DELETE', `/push/${hash}`);
  }
}

function concat(parts) {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
